import React, { useEffect, useMemo, useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { AssessmentOutlined } from '@mui/icons-material';
import useTranslation from 'Hooks/useTranslation';
import usePageSize from 'Hooks/usePageSize';
import useSwitch from 'Hooks/useSwitch';
import SideBarContainer from 'Containers/SideBarContainer';
import { useHasPermission } from 'Utils/permissions';
import { Button, Pagination, StatusIndicator, SearchBox, FilterSelect } from 'Components/ui';
import { DataTable } from 'Components/ui';
import SelectCoursesDialog from 'Components/SelectCoursesDialog';
import {
  BulkActions,
  BulkBar,
  BulkCount,
  Header,
  LoadingState,
  PageBody,
  Subtitle,
  Title,
  Toolbar,
} from 'Components/ui/layout';
import useJobTitle from './Hooks/useJobTitle';
import useRoute from './Hooks/useRoute';
import useCourseAssignment from './Hooks/useCourseAssignment';

const DEFAULT_PAGE_SIZE = 15;

/**
 * Selección de puestos para definir sus cursos.
 *
 * Dos caminos desde aquí: entrar a un puesto para verlo en detalle, o marcar
 * varios y asignarles cursos de una vez. El listado llega completo, así que la
 * búsqueda, el filtro y la paginación se resuelven en el navegador.
 */
export default function DefinitionSelectJobTitle() {
  const { t: translate } = useTranslation();
  const { t2 } = useTranslation('JobTitleCoursesReport');
  const history = useHistory();
  const location = useLocation();

  // Mismo permiso que ya gobierna el resto de reportes.
  const canViewReport = useHasPermission('report.read');

  const [isLoading, setIsLoading] = useState(true);
  const { jobTitle } = useJobTitle(setIsLoading);
  const { handlePage } = useRoute();
  const { active: dialogOpen, activate: openDialog, deactivate: closeDialog } = useSwitch();
  const {
    courses,
    loadingCourses,
    assigning,
    assignCourses,
    pagination,
    page: coursesPage,
    pageSize: coursesPageSize,
    search: coursesSearch,
    setPage: setCoursesPage,
    setPageSize: setCoursesPageSize,
    setSearch: setCoursesSearch,
  } = useCourseAssignment();

  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState([]);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());

  // Los departamentos salen del propio listado: pedir el catálogo entero
  // ofrecería filtrar por departamentos sin un solo puesto.
  const departments = useMemo(
    () =>
      [...new Set(jobTitle.map(row => row.department).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b))
        .map(name => ({ value: name, label: name })),
    [jobTitle]
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return jobTitle.filter(row => {
      const matchesSearch = needle === '' || row.name?.toLowerCase().includes(needle);
      const matchesDepartment = departmentFilter.length === 0 || departmentFilter.includes(row.department);
      return matchesSearch && matchesDepartment;
    });
  }, [jobTitle, search, departmentFilter]);

  const [pageSize, setPageSizeState] = usePageSize('definitionSelectJobTitle', DEFAULT_PAGE_SIZE);

  // Cambiar el tamaño vuelve a la primera página: quien estaba en la 7 con 10
  // por página se saldría del rango al pedir 100, y la tabla saldría vacía.
  const setPageSize = value => {
    setPageSizeState(value);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const visible = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setPage(1);
  }, [search, departmentFilter]);

  // Cualquier cosa que cambie la lista visible descarta la selección. Si no,
  // una asignación en lote podría alcanzar a puestos que el filtro acaba de
  // esconder.
  useEffect(() => {
    setSelected(new Set());
  }, [page, search, departmentFilter]);

  const toggle = jobTitleId => {
    setSelected(previous => {
      const next = new Set(previous);
      if (next.has(jobTitleId)) next.delete(jobTitleId);
      else next.add(jobTitleId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(previous => {
      const ids = visible.map(row => row.id);
      const all = ids.length > 0 && ids.every(id => previous.has(id));
      return all ? new Set() : new Set(ids);
    });
  };

  /**
   * Asigna los cursos elegidos a los puestos marcados. Si el guardado falla, el
   * diálogo se queda abierto con la selección intacta para reintentar.
   *
   * @param {string[]} courseIds - Cursos elegidos en el diálogo.
   * @returns {Promise<boolean>} Si la asignación se confirmó.
   */
  const assignToSelected = async courseIds => {
    try {
      await assignCourses(courseIds, [...selected]);
      setSelected(new Set());
      return true;
    } catch {
      return false;
    }
  };

  const columns = [
    { key: 'name', label: translate('JobTitle.Name'), width: '2.2fr', strong: true },
    { key: 'department', label: translate('JobTitle.Department'), width: '1.7fr' },
    { key: 'numberOfEmployees', label: translate('JobTitle.TotalEmployees'), width: '1fr' },
    { key: 'numberOfCourses', label: translate('JobTitle.TotalCourses'), width: '1fr' },
    {
      key: 'active',
      label: translate('JobTitle.Status'),
      width: '1.1fr',
      render: row => (
        <StatusIndicator
          status={row.active ? 'active' : 'inactive'}
          label={row.active ? translate('JobTitle.StatusActive') : translate('JobTitle.StatusInactive')}
        />
      ),
    },
  ];

  if (location?.state?.isComingFrom !== '/definicion') {
    return <Redirect to="/definicion" />;
  }

  return (
    <SideBarContainer>
      <PageBody>
        <Header>
          <div>
            <Title>{translate('JobTitle.Title')}</Title>
            <Subtitle>{translate('JobTitle.SubTitle')}</Subtitle>
          </div>
          {canViewReport ? (
            <Button type="button" variant="secondary" onClick={() => history.push('/definicion/puesto/reporte')}>
              <AssessmentOutlined />
              {t2('entryButton')}
            </Button>
          ) : null}
        </Header>

        <Toolbar>
          <SearchBox value={search} placeholder={translate('JobTitle.Search')} onChange={setSearch} />
          <FilterSelect
            value={departmentFilter}
            options={departments}
            placeholder={translate('JobTitle.Department')}
            onChange={setDepartmentFilter}
          />
        </Toolbar>

        {/* La barra solo existe mientras haya selección: fuera de ese momento
            ocuparía sitio para no ofrecer nada. */}
        {selected.size > 0 ? (
          <BulkBar>
            <BulkCount>
              {selected.size} {translate('JobTitle.SelectedCount')}
            </BulkCount>
            <BulkActions>
              <Button type="button" variant="secondary" size="sm" onClick={() => setSelected(new Set())}>
                {translate('JobTitle.BulkClear')}
              </Button>
              <Button type="button" variant="primary" size="sm" onClick={openDialog}>
                {translate('JobTitle.multiselection')}
              </Button>
            </BulkActions>
          </BulkBar>
        ) : null}

        {isLoading ? (
          <LoadingState>{translate('JobTitle.Loading')}</LoadingState>
        ) : (
          <>
            <DataTable
              rows={visible}
              columns={columns}
              labels={{
                empty: translate('JobTitle.Empty'),
                selectAll: translate('JobTitle.SelectAll'),
                viewDetails: translate('JobTitle.ViewDetails'),
              }}
              selection={{ selected, onToggle: toggle, onToggleAll: toggleAll }}
              onViewDetails={handlePage}
            />
            {filtered.length > 0 ? (
              <Pagination
                page={page}
                pageSize={pageSize}
                totalPages={totalPages}
                totalItems={filtered.length}
                onChange={setPage}
                onPageSizeChange={setPageSize}
                labels={{
                  showing: translate('JobTitle.PaginationShowing'),
                  of: translate('JobTitle.PaginationOf'),
                  page: translate('JobTitle.PaginationPage'),
                  prev: translate('JobTitle.PaginationPrev'),
                  next: translate('JobTitle.PaginationNext'),
                  first: translate('JobTitle.PaginationFirst'),
                  last: translate('JobTitle.PaginationLast'),
                  perPage: translate('JobTitle.PaginationPerPage'),
                }}
              />
            ) : null}
          </>
        )}
      </PageBody>

      {/* El catálogo se pagina en el servidor: `useCourseAssignment` cuenta las
          páginas desde cero y el diálogo desde uno. */}
      <SelectCoursesDialog
        open={dialogOpen}
        labels={{
          title: translate('JobTitle.assignCoursesTitle'),
          searchPlaceholder: translate('JobTitle.CourseSearch'),
          name: translate('JobTitle.CourseName'),
          duration: translate('JobTitle.CourseDuration'),
          selectAll: translate('JobTitle.SelectAll'),
          selectedCount: translate('JobTitle.SelectedCount'),
          empty: translate('JobTitle.EmptyCourses'),
          loading: translate('JobTitle.Loading'),
          cancel: translate('JobTitle.assignCoursesCancel'),
          confirm: translate('JobTitle.assignCoursesConfirm'),
          pagination: {
            showing: translate('JobTitle.PaginationShowing'),
            of: translate('JobTitle.PaginationOf'),
            page: translate('JobTitle.PaginationPage'),
            prev: translate('JobTitle.PaginationPrev'),
            next: translate('JobTitle.PaginationNext'),
            first: translate('JobTitle.PaginationFirst'),
            last: translate('JobTitle.PaginationLast'),
            perPage: translate('JobTitle.PaginationPerPage'),
          },
        }}
        courses={courses}
        loading={loadingCourses}
        submitting={assigning}
        search={coursesSearch}
        onSearchChange={setCoursesSearch}
        page={coursesPage + 1}
        pageSize={coursesPageSize}
        totalPages={pagination?.totalPages ?? 1}
        totalItems={pagination?.totalItems ?? 0}
        onPageChange={next => setCoursesPage(next - 1)}
        onPageSizeChange={setCoursesPageSize}
        onClose={closeDialog}
        onConfirm={assignToSelected}
      />
    </SideBarContainer>
  );
}
